import React from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DocumentTypeEntry } from "../types";
import { documentIcon } from "./documentIcons";
import { tileClass, tileCornerClass, tileIconClass, tileLabelClass } from "./DocumentTile";

export interface CustomDocumentTileProps {
  entry: DocumentTypeEntry;
  /** Name rows already added for this entry. */
  count: number;
  disabled: boolean;
  onAdd: () => void;
  /** As `DocumentTile`'s: a row on a phone, the count and "+" at its end. */
  compact?: boolean;
}

/**
 * The "Other" tile: each press adds a row to name one more document. The top-left
 * corner counts them; the top-right "+" sits where the other tiles' checkbox does.
 */
const CustomDocumentTile = React.memo(function CustomDocumentTile({
  entry,
  count,
  disabled,
  onAdd,
  compact = false,
}: CustomDocumentTileProps) {
  const Icon = documentIcon(entry.icon);
  // The catalog names the type ("Other"); the tile is the action of adding one.
  const action = `Add ${entry.label} Document`;
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={`${action}${count > 0 ? ` (${count} added)` : ""}`}
      className={cn(
        tileClass(count > 0, disabled, compact),
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      )}
      onClick={onAdd}
    >
      {count > 0 ? (
        <span
          aria-hidden
          className={cn(
            "flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground",
            tileCornerClass("count", compact)
          )}
        >
          {count}
        </span>
      ) : null}
      {/* Decorative: the tile itself is the button that adds a row. */}
      <Plus
        aria-hidden
        className={cn(
          tileCornerClass("add", compact),
          compact ? "size-4 shrink-0 sm:size-5" : "h-6 w-6",
          count > 0 ? "text-primary" : "text-muted-foreground",
          disabled && "opacity-50"
        )}
      />
      <Icon className={tileIconClass(count > 0, compact)} aria-hidden />
      <span className={tileLabelClass(compact)}>{action}</span>
    </button>
  );
});

export default CustomDocumentTile;
